// packages/webapp/src/lib/firebase-client.js
import { initializeApp } from 'firebase/app'; 
import { 
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup, 
  GoogleAuthProvider,
  signOut 
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore"; 
import { getStorage, connectStorageEmulator } from "firebase/storage";

import { isBrowser } from './firebase-utils';

// --- Firebase Config Object --- 
// Populated when initialization is triggered
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID
};


// Single instance pattern for services
let app = null; 
let auth = null;
let db = null;
let storage = null;
let initializationAttempted = false;

// --- Initialization Function (To be called by AuthProvider) --- 

// Renamed internal function - initializes App and Services
function _initializeFirebase() { 
  if (!isBrowser() || initializationAttempted) {
    return;
  }
  initializationAttempted = true; 
  
  console.log("[firebase-client] Attempting Firebase Initialization...");

  // --- Populate Config & Check Validity ---
  firebaseConfig.apiKey = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
  firebaseConfig.authDomain = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN;
  firebaseConfig.projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  firebaseConfig.storageBucket = process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET;
  firebaseConfig.messagingSenderId = process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID;
  firebaseConfig.appId = process.env.NEXT_PUBLIC_FIREBASE_APP_ID;
  
  let configValid = true;
  const requiredKeys = ['apiKey', 'authDomain', 'projectId']; 
  requiredKeys.forEach(key => {
      if (!firebaseConfig[key]) {
          console.error(`[firebase-client] Firebase config missing required key: ${key}.`);
          configValid = false;
      }
  });

  if (!configValid) {
     console.error("[firebase-client] Firebase config invalid. Initialization aborted.");
     app = null; auth = null; db = null; storage = null;
     return; 
  }
  // --- End Config Check ---

  try {
    console.log('[firebase-client] Config being used:', JSON.stringify(firebaseConfig, (key, value) => key === 'apiKey' ? '***REDACTED***' : value));
    console.log("[firebase-client] Initializing Firebase App...");
    const initializedApp = initializeApp(firebaseConfig);
    app = initializedApp;
    console.log("[firebase-client] Firebase App Initialized.");
    console.log("[firebase-client] Registered Firebase providers:", Object.keys(app._container?.providers || {}));

    // Initialize services immediately instead of with a delay
    console.log("[firebase-client] Initializing services...");
    try {
        console.log("[firebase-client] Initializing Firebase Auth using getAuth...");
        auth = getAuth(initializedApp);
        console.log("[firebase-client] Firebase Auth Initialized (using getAuth).");
    } catch(authError) {
        console.error("[firebase-client] Failed to initialize Firebase Auth with getAuth:", authError);
        auth = null; 
    }

    try {
        console.log("[firebase-client] Initializing Firestore...");
        db = getFirestore(initializedApp);
        console.log("[firebase-client] Firestore Initialized.");
    } catch(dbError) {
        console.error("[firebase-client] Failed to initialize Firestore:", dbError);
        db = null; 
    }

    if (firebaseConfig.storageBucket) { 
      try {
          console.log("[firebase-client] Initializing Firebase Storage...");
          storage = getStorage(initializedApp);
          console.log("[firebase-client] Firebase Storage Initialized.");
      } catch(storageError) {
          console.error("[firebase-client] Failed to initialize Firebase Storage:", storageError);
          storage = null; 
      }
    } else {
        console.warn("[firebase-client] storageBucket not configured. Skipping Storage initialization.");
        storage = null;
    }

    // Connect to emulators if enabled
    if (process.env.NEXT_PUBLIC_FIREBASE_USE_EMULATOR === 'true') {
        console.log("[firebase-client] Connecting to Emulators...");
        const host = process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST || 'localhost';
        try {
            if (auth) {
                const authPort = parseInt(process.env.NEXT_PUBLIC_FIREBASE_AUTH_PORT || '9099', 10);
                console.log(`[firebase-client] Connecting Auth emulator to http://${host}:${authPort}`);
                connectAuthEmulator(auth, `http://${host}:${authPort}`, { disableWarnings: true });
            } else {
                console.warn("[firebase-client] Skipping Auth emulator connection: Auth instance is null.");
            }
            if (db) { 
                 const firestorePort = parseInt(process.env.NEXT_PUBLIC_FIREBASE_FIRESTORE_PORT || '8082', 10);
                 console.log(`[firebase-client] Connecting Firestore emulator to ${host}:${firestorePort}`);
                connectFirestoreEmulator(db, host, firestorePort);
            }
            if (storage) { 
                const storagePort = parseInt(process.env.NEXT_PUBLIC_FIREBASE_STORAGE_PORT || '9199', 10);
                 console.log(`[firebase-client] Connecting Storage emulator to ${host}:${storagePort}`);
                connectStorageEmulator(storage, host, storagePort);
            }
             console.log("[firebase-client] Emulator connections completed.");
        } catch (emulatorError) {
             console.error("[firebase-client] Error connecting to Firebase emulators:", emulatorError);
        }
    } else {
        console.log("[firebase-client] Using LIVE Firebase project (emulator mode disabled)");
        console.log(`[firebase-client] Connected to Firebase project: ${firebaseConfig.projectId}`);
    }

  } catch (appError) {
    console.error("[firebase-client] Failed to initialize Firebase app:", appError);
    app = null; auth = null; db = null; storage = null;
  }
}

export { _initializeFirebase };

// --- Expose Getters ---
export function getFirebaseAuth() {
    if (!initializationAttempted) {
         console.warn("[firebase-client] getFirebaseAuth called before initialization was attempted by AuthProvider.");
    }
    return auth; 
}

export function getFirestoreDb() {
   if (!initializationAttempted) {
         console.warn("[firebase-client] getFirestoreDb called before initialization was attempted by AuthProvider");
    }
    return db;
}

export function getFirebaseStorage() {
   if (!initializationAttempted) {
         console.warn("[firebase-client] getFirebaseStorage called before initialization was attempted by AuthProvider");
    }
    return storage;
}

// --- Centralized Auth Helpers ---

// Sign in with Email and Password
export function signIn(email, password) {
  const currentAuth = getFirebaseAuth();
  if (!currentAuth) {
    console.error("Firebase auth not available for signIn");
    return Promise.reject(new Error("Firebase auth not available"));
  }
  return signInWithEmailAndPassword(currentAuth, email, password);
}

// Sign in with Google Popup
export function signInWithGoogle() {
  const currentAuth = getFirebaseAuth();
  if (!currentAuth) {
    console.error("Firebase auth not available for signInWithGoogle");
    return Promise.reject(new Error("Firebase auth not available"));
  }
  const provider = new GoogleAuthProvider();
  
  // Add these lines to log debug info and specify redirect URL
  console.log("Google auth provider created", provider);
  provider.setCustomParameters({
    'prompt': 'select_account',
    'login_hint': 'user@example.com'
  });
  
  return signInWithPopup(currentAuth, provider)
    .catch(error => {
      console.error("Google sign-in error:", error);
      throw error;
    });
}

// Register/Sign Up with Email and Password
export function signUp(email, password) {
  const currentAuth = getFirebaseAuth();
  if (!currentAuth) {
    console.error("Firebase auth not available for signUp");
    return Promise.reject(new Error("Firebase auth not available"));
  }
  return createUserWithEmailAndPassword(currentAuth, email, password);
}

// Log out
export function logOut() {
  const currentAuth = getFirebaseAuth();
  if (!currentAuth) {
    console.error("Firebase auth not available for logOut");
    return Promise.reject(new Error("Firebase auth not available"));
  }
  return signOut(currentAuth);
} 